import { StudioSystemType } from 'types/global'
import { FeatureChecker } from 'util/featureFlags'
import { getPanelLayoutDimensions, hasUniformRowLengths } from './compatibilityLayout'
import { validateNovaCompatibility } from './mounting_systems/Nova/NovaCompatibility'
import { validateSolarNestGroundMountCompatibility } from './mounting_systems/SolarNestGroundMount'
import { validateSolarNestRooftopCompatibility } from './mounting_systems/SolarNestRooftop'
import { checkSolarPortCompatibility } from './mounting_systems/SolarPort/SolarPortCompatibility'
import { validateVanDerValkCompatibility, getVanDerValkRoofTypes } from './mounting_systems/VanDerValk'
import { getVanDerValkFeatureFlag } from '../vandervalk/vanDerValkMountingMode'
import { CompatibilityParameters } from './types'

export { getPanelLayoutDimensions, hasUniformRowLengths }

export const getSystemRoofType = (): string | null => {
  const projectData = window.projectForm.getState().values
  var roofTypeName
  const roofTypeUrl = projectData.roof_type
  if (roofTypeUrl?.length) {
    let roofId = Number(roofTypeUrl.split('roof_types/')[1].split('/')[0])
    roofTypeName = window.AccountHelper.getRoofTypeById(roofId)?.name
  }
  return roofTypeName ? roofTypeName : null
}

function getRoofSlopeCompatibility(system: StudioSystemType, slopeRange: [number, number]): boolean {
  const roofSlopesAreCompatible = system.moduleGrids().every((moduleGrid) => {
    const slope = Math.round(moduleGrid.getSlope() * 10) / 10 // Round to 1 decimal place due to floating-point precision issue
    return slope <= slopeRange[1] && slope >= slopeRange[0]
  })
  return roofSlopesAreCompatible
}

function getCompatibility(
  system: StudioSystemType,
  compatibilityParameters: CompatibilityParameters,
  isFeatureEnabled: FeatureChecker
): boolean {
  const mountingName = system?.mounting
  if (!mountingName) return true

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
    system.uuid,
    'plugin'
  )

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(
    `${mountingName.toUpperCase()}_INPUT_WARNING`,
    system.uuid,
    'plugin'
  )

  const featureFlagToCheck =
    mountingName === 'vanDerValk' ? getVanDerValkFeatureFlag(system.mounting_type) : compatibilityParameters.featureFlag

  if (featureFlagToCheck) {
    const isFeatureFlagEnabled = isFeatureEnabled(featureFlagToCheck, 'on')
    if (!isFeatureFlagEnabled) {
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: window.translate('This mounting system is not enabled. Please contact support to enable it.'),
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (compatibilityParameters.slopeRange) {
    if (!getRoofSlopeCompatibility(system, compatibilityParameters.slopeRange)) {
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: window.translate(
          'Mounting system is incompatible with roof slope. Roof slopes must be between %{min}-%{max} degrees.',
          { min: compatibilityParameters.slopeRange[0], max: compatibilityParameters.slopeRange[1] }
        ),
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (hasIncompatibleModules(mountingName, system, compatibilityParameters)) {
    return false
  }

  if (compatibilityParameters.panelLayoutRange) {
    const { columns: colRange, rows: rowRange } = compatibilityParameters.panelLayoutRange
    const allGridsInRange = system.moduleGrids().every((moduleGrid) => {
      const { numColumns, numRows } = getPanelLayoutDimensions(moduleGrid.cellsActive)
      return numColumns >= colRange[0] && numColumns <= colRange[1] && numRows >= rowRange[0] && numRows <= rowRange[1]
    })
    if (!allGridsInRange) {
      const evenDimension = mountingName === 'solarNestRooftop' ? 'rows' : 'columns'
      const layoutParams = { colMin: colRange[0], rowMin: rowRange[0], colMax: colRange[1], rowMax: rowRange[1] }
      const layoutMessage =
        evenDimension === 'rows'
          ? window.translate(
              'Panel layout must be between %{colMin}x%{rowMin} and %{colMax}x%{rowMax} with an even number of rows.',
              layoutParams
            )
          : window.translate(
              'Panel layout must be between %{colMin}x%{rowMin} and %{colMax}x%{rowMax} with an even number of columns.',
              layoutParams
            )
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: layoutMessage,
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (mountingName === 'solarPortGroundMount') {
    if (!checkSolarPortCompatibility(system, window.translate)) return false
  }

  if (compatibilityParameters.roofHeightRange) {
    // TODO!
  }

  if (compatibilityParameters?.roofTypeRequired) {
    const roofType = getSystemRoofType()

    if (!roofType) {
        if (mountingName === 'unirac') {
          window.WorkspaceHelper.addProjectErrorToReduxStore({
            message: window.translate('Roof Type must be selected in the dropdown above to enable integration.'),
            key: `${mountingName.toUpperCase()}_ERROR`,
            severity: 'warning',
            systemId: system.uuid,
            source: 'mounting-integration',
            category: 'mounting',
            options: {},
          })
        } else {
          window.WorkspaceHelper.addProjectErrorToReduxStore({
            message: window.translate('Roof Type must be selected in the dropdown above to enable auto-racking.'),
            key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
            severity: 'warning',
            systemId: system.uuid,
            source: 'plugin',
            category: 'mounting',
            options: {},
          })
        }
      return false
    }
  }

  const roofTypesToValidate =
    mountingName === 'vanDerValk' ? getVanDerValkRoofTypes(system.mounting_type) : compatibilityParameters.roofTypes

  if (roofTypesToValidate?.length) {
    const projectData = window.projectForm.getState().values
    var roofTypeCompatibility: boolean = true
    if (!projectData.roof_type?.length && compatibilityParameters.roofTypeRequired === false) {
      // Pass
    } else {
      var roofTypeName
      const roofTypeUrl = projectData.roof_type
      if (roofTypeUrl?.length) {
        let roofId = Number(roofTypeUrl.split('roof_types/')[1].split('/')[0])
        roofTypeName = window.AccountHelper.getRoofTypeById(roofId)?.name
      }
      if (!roofTypesToValidate.includes(roofTypeName)) {
        roofTypeCompatibility = false
      }
    }
    if (!roofTypeCompatibility) {
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: window.translate(
          'Mounting system is incompatible with roof type. Roof type must be one of the following; %{roofTypes}.',
          { roofTypes: roofTypesToValidate.join(', ') }
        ),
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (compatibilityParameters.relativeTiltsIdentical) {
    const relativeTilts = system.moduleGrids().map((moduleGrid) => Number(moduleGrid.getRacksTilt().toFixed(1)))
    const uniqueRelativeTilts = relativeTilts.filter((v, i, a) => a.indexOf(v) === i)
    if (uniqueRelativeTilts.length > 1) {
      const message = compatibilityParameters.relativeTiltsAllowedValues
        ? window.translate(
            'Mounting system is incompatible with panel relative tilt. Every panel group must have the same relative tilt of %{compatibleDegrees}.',
            {
              compatibleDegrees: compatibilityParameters.relativeTiltsAllowedValues
                .map((value) => `${value}°`)
                .join(', '),
            }
          )
        : window.translate(
            'Mounting system is incompatible with panel relative tilt. Every panel group must have the same relative tilt.'
          )
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: message,
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (compatibilityParameters.relativeTiltsAllowedValues) {
    const relativeTilts = system.moduleGrids().map((moduleGrid) => Number(moduleGrid.getRacksTilt().toFixed(1)))
    const tiltIsCompatible = (relativeTilt: number) =>
      (compatibilityParameters.relativeTiltsAllowedValues ?? []).includes(relativeTilt)
    if (!relativeTilts.every(tiltIsCompatible)) {
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message: window.translate(
          'Mounting system is incompatible with panel relative tilt. Relative tilt must be one of the following: %{compatibleDegrees}.',
          {
            compatibleDegrees: compatibilityParameters.relativeTiltsAllowedValues
              .map((value) => `${value}°`)
              .join(', '),
          }
        ),
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (compatibilityParameters.panelOrientation) {
    var panelOrientationCompatibility: boolean = true
    const panelOrientations = system.moduleGrids().map((moduleGrid) => moduleGrid.moduleLayout())
    const uniquePanelOrientations = panelOrientations.filter((v, i, a) => a.indexOf(v) === i)
    uniquePanelOrientations.forEach((panelOrientation) => {
      if (panelOrientation !== compatibilityParameters.panelOrientation) {
        panelOrientationCompatibility = false
      }
    })
    if (!panelOrientationCompatibility) {
      const orientation = compatibilityParameters.panelOrientation
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message:
          orientation === 'portrait'
            ? window.translate(
                'Mounting system is incompatible with panel orientation. Panel orientation must be portrait.'
              )
            : window.translate(
                'Mounting system is incompatible with panel orientation. Panel orientation must be landscape.'
              ),
        key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  if (mountingName === 'solarNestGroundMount') {
    if (!validateSolarNestGroundMountCompatibility(system)) {
      return false
    }
  }
  if (mountingName === 'solarNestRooftop') {
    if (!validateSolarNestRooftopCompatibility(system)) {
      return false
    }
  }
  if (mountingName === 'nova' && !validateNovaCompatibility(system)) {
    return false
  }
  if (mountingName === 'vanDerValk') {
    if (!validateVanDerValkCompatibility(system)) {
      return false
    }
  }

  return true
}

const hasIncompatibleModules = (
  mountingName: string,
  system: StudioSystemType,
  compatibilityParameters: CompatibilityParameters
): boolean => {
  return system.moduleGrids().some((mg) => {
    const moduleType = mg.moduleType()

    if (compatibilityParameters.moduleHasSpecs) {
      let missingSpecs: string[] = []
      compatibilityParameters.moduleHasSpecs.forEach((spec) => {
        if (typeof moduleType[spec] === 'undefined' || moduleType[spec] === null) {
          missingSpecs.push(spec)
        }
      })
      if (missingSpecs.length) {
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message: window.translate(
            'Module %{code} is missing %{missingSpecs} from its specs. Please update the component in the Control > Design & Hardware section or contact us to update our database.',
            { code: moduleType.code, missingSpecs: missingSpecs.join(', ') }
          ),
          key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
          severity: 'warning',
          systemId: system.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        return true
      }
    }

    if (compatibilityParameters.moduleThicknessRange) {
      const moduleThickness = moduleType.thickness
      if (
        moduleThickness &&
        (moduleThickness < compatibilityParameters.moduleThicknessRange[0] ||
          moduleThickness > compatibilityParameters.moduleThicknessRange[1])
      ) {
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message: window.translate(
            'Mounting system is incompatible with module thickness. Module thickness must be between %{min}-%{max} mm.',
            {
              min: compatibilityParameters.moduleThicknessRange[0],
              max: compatibilityParameters.moduleThicknessRange[1],
            }
          ),
          key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
          severity: 'warning',
          systemId: system.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        return true
      }
    }

    if (compatibilityParameters.moduleWidthRanges) {
      const moduleWidth = moduleType.width
      const incompatibleWithModuleWidth =
        moduleWidth &&
        !compatibilityParameters.moduleWidthRanges.reduce(
          (accumulator, range) => (moduleWidth * 1000 >= range[0] && moduleWidth * 1000 <= range[1]) || accumulator,
          false
        )
      if (incompatibleWithModuleWidth) {
        const ranges = compatibilityParameters.moduleWidthRanges.map((range) => `${range[0]}-${range[1]} mm`).join(' ')
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message: window.translate(
            'Mounting system is incompatible with module width. Module width must be between %{ranges}.',
            { ranges }
          ),
          key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
          severity: 'warning',
          systemId: system.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        return true
      }
    }

    if (compatibilityParameters.moduleHeightRange) {
      const moduleHeight = moduleType.height
      if (
        moduleHeight &&
        (moduleHeight * 1000 < compatibilityParameters.moduleHeightRange[0] ||
          moduleHeight * 1000 > compatibilityParameters.moduleHeightRange[1])
      ) {
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message: window.translate(
            'Mounting system is incompatible with module height. Module height must be between %{min}-%{max} mm.',
            { min: compatibilityParameters.moduleHeightRange[0], max: compatibilityParameters.moduleHeightRange[1] }
          ),
          key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
          severity: 'warning',
          systemId: system.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        return true
      }
    }
    return false
  })
}

export default getCompatibility
